<!-- Meldung, wenn Bilder nicht gelöscht werden können, weil sie in einem Design stecken. -->
<template>
  <Teleport to="body">
    <div v-if="error" class="uiu-backdrop" @click.self="emit('close')">
      <div class="uiu-panel" role="alertdialog" aria-modal="true" aria-labelledby="uiu-title" aria-describedby="uiu-text">
        <div class="uiu-head">
          <span class="uiu-icon" aria-hidden="true"><Lock :size="18" /></span>
          <h2 id="uiu-title" class="uiu-title">{{ title }}</h2>
        </div>

        <p id="uiu-text" class="uiu-text">
          {{ blockedCount === 1 ? 'Es steckt' : 'Sie stecken' }} in {{ designs.length === 1 ? 'diesem Design' : 'diesen Designs' }}
          und {{ blockedCount === 1 ? 'kann' : 'können' }} deshalb nicht gelöscht werden.
          Tauschen Sie {{ blockedCount === 1 ? 'das Bild' : 'die Bilder' }} im Designer aus oder löschen Sie das Design.
        </p>

        <ul v-if="designs.length" class="uiu-list">
          <li v-for="d in designs" :key="d.id" class="uiu-item">
            <span class="uiu-item__icon" aria-hidden="true"><Sparkles :size="16" /></span>
            <span class="uiu-item__copy">
              <strong class="uiu-item__name">{{ blockingDesignLabel(d) }}</strong>
              <span v-if="d.name && d.productTitle" class="uiu-item__meta">{{ d.productTitle }}</span>
            </span>
            <button type="button" class="uiu-btn uiu-btn--small" @click="emit('open-design', d)">
              Im Designer öffnen
            </button>
          </li>
        </ul>

        <p v-if="deletedCount > 0" class="uiu-note">
          {{ deletedCount === 1 ? 'Das andere ausgewählte Bild wurde gelöscht.' : `Die übrigen ${deletedCount} Bilder wurden gelöscht.` }}
        </p>

        <div class="uiu-actions">
          <button ref="okBtn" type="button" class="uiu-btn uiu-btn--primary" @click="emit('close')">Verstanden</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { Lock, Sparkles } from 'lucide-vue-next'
import { blockingDesignLabel, type BlockingDesign, type UploadInUseError } from '../../services/uploadService'

const props = defineProps<{ error: UploadInUseError | null }>()
const emit = defineEmits<{ (e: 'close'): void; (e: 'open-design', design: BlockingDesign): void }>()

const okBtn = ref<HTMLButtonElement | null>(null)
const designs = computed(() => props.error?.designs ?? [])
const blockedCount = computed(() => props.error?.blockedIds.length ?? 0)
const deletedCount = computed(() => props.error?.deletedIds.length ?? 0)
const title = computed(() => blockedCount.value === 1 ? 'Bild wird noch verwendet' : `${blockedCount.value} Bilder werden noch verwendet`)

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('close')
}
watch(() => props.error, (err) => {
  if (err) {
    window.addEventListener('keydown', onKey)
    void nextTick(() => okBtn.value?.focus())
  } else {
    window.removeEventListener('keydown', onKey)
  }
})
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>

<style scoped>
.uiu-backdrop{
  position: fixed;
  inset: 0;
  z-index: 100050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(2,6,23,.45);
}
.uiu-panel{
  width: min(460px, 100%);
  background: #fff;
  border: 1px solid rgba(15,23,42,.10);
  box-shadow: 0 22px 70px rgba(2,6,23,.22);
  border-radius: 4px;
  padding: 20px;
  display: grid;
  gap: 12px;
  color: #141414;
}
.uiu-head{ display: flex; align-items: center; gap: 12px; }
.uiu-icon{
  flex: 0 0 38px;
  height: 38px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(217,119,6,.12);
  color: #b45309;
}
.uiu-title{ margin: 0; font-size: 17px; font-weight: 700; line-height: 1.3; }
.uiu-text{ margin: 0; font-size: 14px; line-height: 1.5; color: rgba(15,23,42,.72); }
.uiu-list{ list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.uiu-item{
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid rgba(15,23,42,.12);
  border-radius: 4px;
  background: #fafafa;
}
.uiu-item__icon{ color: #7c3aed; display: inline-flex; }
.uiu-item__copy{ flex: 1 1 auto; min-width: 0; display: grid; gap: 1px; }
.uiu-item__name{ font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.uiu-item__meta{ font-size: 12px; color: rgba(15,23,42,.6); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.uiu-note{ margin: 0; font-size: 13px; color: #15803d; }
.uiu-actions{ display: flex; justify-content: flex-end; }
.uiu-btn{
  min-height: 38px;
  padding: 0 16px;
  border: 2px solid rgba(15,23,42,.16);
  border-radius: 4px;
  background: #fff;
  color: #18212f;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}
.uiu-btn:hover{ border-color: rgba(15,23,42,.32); }
.uiu-btn--small{ min-height: 32px; padding: 0 10px; font-size: 12px; }
.uiu-btn--primary{ background: #e30b17; border-color: #e30b17; color: #fff; }
.uiu-btn--primary:hover{ background: #c40913; border-color: #c40913; }
@media (max-width: 480px){
  .uiu-item{ flex-wrap: wrap; }
  .uiu-btn--small{ width: 100%; }
}
</style>
